/**
 * Payment Service - Handles payment processing through Dreamscape storefront
 * Based on how sandomains.com and other Dreamscape storefronts handle payments
 */

import { createClient } from './dreamscape-sdk';

export interface PaymentRedirectParams {
  orderId: string | number;
  customerId: number;
  amount: number;
  currency: string;
  domainName?: string;
  returnUrl?: string;
  cancelUrl?: string;
}

export interface PaymentResult {
  success: boolean;
  orderId?: string;
  invoiceId?: number;
  message?: string;
}

const getStorefrontUrl = () => {
  if (import.meta.env.VITE_DREAMSCAPE_STOREFRONT_URL) return import.meta.env.VITE_DREAMSCAPE_STOREFRONT_URL;
  return typeof window !== 'undefined' ? window.location.origin : 'https://sandomains.com';
};

/**
 * Generate storefront password reset URL
 * Redirects to Dreamscape storefront (e.g., sandomains.com) dedicated reset endpoint
 */
export function getStorefrontPasswordResetUrl(email: string, customerId?: number): string {
  const baseUrl = getStorefrontUrl();
  const resetPath = import.meta.env.VITE_DREAMSCAPE_STOREFRONT_RESET_PATH || '/login/forgot-password';
  const params = new URLSearchParams({
    email: email.trim().toLowerCase(),
    ...(customerId && !isNaN(customerId) ? { customer_id: customerId.toString() } : {}),
    return_url: `${window.location.origin}/login`,
  });
  return `${baseUrl}${resetPath}?${params.toString()}`;
}

/**
 * Generate storefront payment URL
 * This redirects to the Dreamscape storefront payment page (like sandomains.com)
 * where customers can pay using PayExpress (credit cards) or PayPal
 */
export function getStorefrontPaymentUrl(params: PaymentRedirectParams): string {
  const baseUrl = getStorefrontUrl();

  // Build payment URL with parameters
  const paymentParams = new URLSearchParams({
    order_id: params.orderId.toString(),
    customer_id: params.customerId.toString(),
    amount: params.amount.toString(),
    currency: params.currency,
    ...(params.domainName && { domain: params.domainName }),
    ...(params.returnUrl && { return_url: params.returnUrl }),
    ...(params.cancelUrl && { cancel_url: params.cancelUrl }),
  });

  // Storefront payment endpoint (typically /checkout or /payment)
  const paymentPath = '/checkout/payment';
  return `${baseUrl}${paymentPath}?${paymentParams.toString()}`;
}

/**
 * Get invoice payment URL from API
 * This uses the Dreamscape API to get the payment URL for an invoice
 */
export async function getInvoicePaymentUrl(invoiceId: number): Promise<string | null> {
  try {
    const client = createClient();
    const invoice = await client.finances.getInvoiceDetails(invoiceId);

    // Check if invoice has a payment URL
    // The API might return a payment_url or checkout_url in the invoice response
    if ((invoice as any).payment_url) {
      return (invoice as any).payment_url;
    }

    if ((invoice as any).checkout_url) {
      return (invoice as any).checkout_url;
    }

    // If no direct URL, construct it from invoice data
    if ((invoice as any).id && (invoice as any).customer_id) {
      const baseUrl = import.meta.env.VITE_DREAMSCAPE_STOREFRONT_URL || 'https://sandomains.com';
      return `${baseUrl}/checkout/payment?invoice_id=${invoiceId}`;
    }

    return null;
  } catch (error) {
    console.error('Error getting invoice payment URL:', error);
    return null;
  }
}

/**
 * Redirect to storefront payment page
 */
export function redirectToPayment(params: PaymentRedirectParams): void {
  const paymentUrl = getStorefrontPaymentUrl(params);
  window.location.href = paymentUrl;
}

/**
 * Process payment callback (called after payment is completed on storefront)
 * This verifies the payment status with the API
 */
export async function verifyPaymentStatus(orderId: string | number): Promise<PaymentResult> {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('dummy') === 'true') {
      return {
        success: true,
        orderId: orderId.toString(),
        message: 'Dummy Payment Verified successfully',
      };
    }

    const client = createClient();

    // Get domain details to check if payment was successful
    // If domain registration succeeded, payment was successful
    const domains = await client.domains.getAll({
      limit: 100
    });

    // Find the domain by order ID (domain ID should match order ID)
    // Collection uses 'items' not 'data'
    const domain = domains.items?.find((d: any) =>
      d.id?.toString() === orderId.toString() ||
      d.order_id?.toString() === orderId.toString()
    );

    if (domain) {
      return {
        success: true,
        orderId: orderId.toString(),
        message: 'Payment verified successfully',
      };
    }

    // Check invoice status
    try {
      const invoiceId = parseInt(orderId.toString());
      if (!isNaN(invoiceId)) {
        const invoice = await client.finances.getInvoiceDetails(invoiceId);

        // Check if invoice is paid
        if ((invoice as any).status === 'paid' || (invoice as any).paid === true) {
          return {
            success: true,
            orderId: orderId.toString(),
            invoiceId: invoiceId,
            message: 'Payment verified successfully',
          };
        }
      }
    } catch (invoiceError) {
      // Invoice check failed, continue with domain check result
      console.warn('Could not verify invoice status:', invoiceError);
    }

    return {
      success: false,
      orderId: orderId.toString(),
      message: 'Payment verification pending or failed',
    };
  } catch (error) {
    console.error('Error verifying payment:', error);
    return {
      success: false,
      orderId: orderId.toString(),
      message: error instanceof Error ? error.message : 'Failed to verify payment',
    };
  }
}

/**
 * Get payment status from URL parameters (after redirect from storefront)
 */
export function getPaymentStatusFromUrl(): {
  success: boolean;
  orderId?: string;
  invoiceId?: string;
  error?: string;
} {
  const urlParams = new URLSearchParams(window.location.search);

  const orderId = urlParams.get('order_id') || urlParams.get('orderId');
  const invoiceId = urlParams.get('invoice_id') || urlParams.get('invoiceId');
  const success = urlParams.get('success') === 'true' || urlParams.get('status') === 'success';
  const error = urlParams.get('error') || urlParams.get('error_message');

  return {
    success: success && !!orderId,
    orderId: orderId || undefined,
    invoiceId: invoiceId || undefined,
    error: error || undefined,
  };
}

